import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { CreateUserDialog, EditUserDialog } from "../components/users/UserDialogs";
import { isMaster, schoolName } from "../components/SchoolSelect";
const roleColors = {
  admin: "bg-indigo-100 text-indigo-800",
  principal: "bg-emerald-100 text-emerald-800",
  teacher: "bg-sky-100 text-sky-800",
  parent: "bg-amber-100 text-amber-800",
  staff: "bg-zinc-200 text-zinc-800",
  accounts: "bg-rose-100 text-rose-800",
};

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">People & Staff</h1>
          <p className="text-zinc-500 mt-1">Manage administrators, teachers, parents and support staff.</p>
        </div>
        {me.role === "admin" && <CreateUserDialog onDone={load} master={isMaster(me)} />}
      </div>

      <Card className="border-zinc-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead>
              <TableHead>Role</TableHead><TableHead>Access</TableHead><TableHead>Phone</TableHead>
              {me.role === "admin" && <TableHead className="w-20"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} className="row-hover">
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-zinc-600">{u.email}</TableCell>
                <TableCell>
                  <span className={`text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wide ${roleColors[u.role]}`}>
                    {u.role}
                  </span>
                </TableCell>
                <TableCell className="text-zinc-500 text-sm" data-testid={`user-access-${u.email}`}>
                  {isMaster(me) && <div className="text-xs font-semibold text-indigo-700">{schoolName(u.school_id)}</div>}
                  {u.role === "teacher" ? (u.assigned_classes?.length ? `Classes ${u.assigned_classes.join(", ")}` : "No classes yet")
                    : u.role === "parent" ? `${u.linked_student_ids?.length || 0} child(ren)` : "—"}
                </TableCell>
                <TableCell className="text-zinc-500">{u.phone || "—"}</TableCell>
                {me.role === "admin" && (
                  <TableCell className="flex gap-2">
                    <button onClick={() => setEditing(u)} className="text-zinc-400 hover:text-indigo-700 transition-colors" data-testid={`edit-user-${u.email}`}>
                      <PencilSimple size={16} />
                    </button>
                    {u.id !== me.id && (
                      <button onClick={() => remove(u.id)} className="text-zinc-400 hover:text-rose-600 transition-colors" data-testid={`delete-user-${u.email}`}>
                        <Trash size={16} />
                      </button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-500">No users yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <EditUserDialog user={editing} open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }} onDone={load} />
    </div>
  );
}
